<template>
  <BaseTopBar
    v-if="isMounted"
    v-hotkey="shortcuts"
    :left-panel-width="leftPanelWidth"
    :is-navigation-displayed="isNavigationDisplayed"
    is-hideable
    with-find
    @find="$emit('find')"
  >
    <template #left>
      <Breadcrumb
        :items="hierarchy || []"
        :remove-parent-action="removeParentAction"
        :home="breadcrumbHome"
      />
    </template>
    <template #right>
      <div
        v-for="action in mainActions"
        :key="action.text"
        :ref="
          (el) => {
            if (action === taskActions.plan && el) {
              setTriggerPlanEl(
                (el as { $el?: HTMLElement }).$el || (el as HTMLElement)
              );
            }
          }
        "
      >
        <TaskAction
          :display-recurrence="displayRecurrence"
          :display-plan="displayPlan"
          :task-status="taskStatus"
          :action="action"
          :task="task"
          show-unplan-cross
          @click="getActionFunc(action, $event)"
          @unplan="getActionFunc(taskActions.unplan)"
        />
      </div>
      <RecordingToolbar
        v-if="showRecordingToolbar"
        v-bind="recordingToolbarProps"
        @recording-control-click="onRecordingControlClick"
        @toggle-transcript="toggleTranscriptPopover"
        @close-transcript="closeRecordingTranscript"
      />
      <ShareButton
        :root="shareRoot"
        :organization-id="personalOrganizationId"
        :export-action="onExport"
        :tint="'none'"
      />
      <BaseButton
        kind="auxiliary"
        :text="isPinned ? 'Unpin' : 'Pin'"
        @click="togglePin"
      >
        <template #icon>
          <PinIcon :class="{ pinned: isPinned }" />
        </template>
      </BaseButton>
    </template>
    <template #more>
      <div
        :ref="
          (el) => {
            if (el) {
              setTriggerMoreActionsEl(
                (el as { $el?: HTMLElement }).$el || (el as HTMLElement)
              );
            }
          }
        "
      />
      <BaseButton
        v-for="action in moreActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="action.text"
        :state="action.state || 'default'"
        :tint="action.tint || 'none'"
        is-expanded
        @click="getActionFunc(action, $event)"
      >
        <template #icon> <component :is="action.icon" /> </template
        ><template #right>
          {{ action.shortcutsDisplay }}
        </template>
      </BaseButton>
    </template>
  </BaseTopBar>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import { FullTask, Recurrence, Week } from "@ctrl/controller-rpc";

import { taskActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, { type BreadcrumbHome } from "@/components/Breadcrumb.vue";
import { TaskContextActions } from "@/components/context-menu/context-actions";
import PinIcon from "@/components/icons/PinIcon.vue";
import RecurringIcon from "@/components/icons/RecurringIcon.vue";
import * as inject from "@/components/inject";
import type { ExportRoot } from "@/components/marketplace/export";
import { createBuiltinExportAction } from "@/components/modals/export";
import { getTaskShortcuts } from "@/components/object-shortcuts";
import RecordingToolbar from "@/components/RecordingToolbar.vue";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  selectAll as selectAllShortcut,
  Shortcuts,
} from "@/components/shortcuts";
import { useAudioRecording } from "@/composables/recording/useAudioRecording";
import { useMeetingRecording } from "@/composables/recording/useMeetingRecording";
import { useRecordingToolbar } from "@/composables/recording/useRecordingToolbar";
import type { MeetingRecordingContext } from "@/recording/types";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { displayRecurrenceDate, useDisplaySchedule } from "@/task";
import { computeHierarchy } from "@/utils/hierarchy";
import { makeMove } from "@/utils/move";
import { navigateBackward, navigateToObject } from "@/utils/navigate";

import { findNextAllocation } from "../utils";
import TaskAction from "./TaskAction.vue";

export default defineComponent({
  components: {
    BaseButton,
    BaseTopBar,
    Breadcrumb,
    RecordingToolbar,
    RecurringIcon,
    ShareButton,
    TaskAction,
    PinIcon,
  },
  props: {
    hasBlockActive: { type: Boolean, required: true },
    isNavigationDisplayed: { type: Boolean, default: true },
    leftPanelWidth: { type: Number, default: 0 },
    // The task screen only hosts native tasks: bound tasks (graph rows
    // surfaced as tasks) redirect to their object screen in `routes/Task.vue`.
    task: {
      type: Object as PropType<FullTask>,
      required: true,
    },
  },
  emits: {
    checkTask: () => true,
    find: () => true,
    flush: () => true,
    selectAll: () => true,
  },
  async setup(props, { emit }) {
    const billing = inject.billing();
    const datePicker = inject.datePicker();
    const recurrencePicker = inject.recurrencePicker();
    const assigneePicker = inject.assigneePicker();
    const console = inject.console();
    const controller = inject.controller();
    const profile = inject.profile();
    const baseLogger = inject.logger();
    const destinationPicker = inject.destinationPicker();
    const move = makeMove(controller, profile, baseLogger, destinationPicker);
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const simpleModal = inject.simpleModal();
    const menu = inject.menu();
    const currentWorkspaceId = inject.currentWorkspaceId();
    const locale = inject.locale();
    const palette = inject.palette();
    const automationHotkeys = inject.automationHotkeys();
    const router = inject.router();
    const flush = () => emit("flush");
    const selectAll = () => emit("selectAll");
    const { hasBlockActive, task } = toRefs(props);
    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));
    const schedulable = computed(() => !task.value.completed);
    const releaser = useReleaser();

    // Automation hotkeys run on the object being viewed: arm them with this
    // task while the page is mounted (same input as Mod+K → Run automation).
    const deactivateAutomationHotkeys = automationHotkeys.value?.activate(
      () => ({
        kind: "reference",
        referenceId: { kind: "task", id: task.value.id },
      })
    );
    if (deactivateAutomationHotkeys)
      releaser.defer(deactivateAutomationHotkeys);

    // Stop/transcript controls for a meeting recording targeting this task
    // (started via the notes editor's /meet command). Shown only while this
    // task owns the active recording — mirrors CustomObjectTopBar.
    const meetingEvent = computed<MeetingRecordingContext>(() => ({
      id: task.value.id,
      title: task.value.title.trim(),
      target: { kind: "task" },
    }));
    const audioRecording = releaser.add(useAudioRecording(baseLogger));
    const meetingRecording = useMeetingRecording(
      baseLogger,
      billing,
      audioRecording,
      meetingEvent
    );
    const showRecordingToolbar = computed(
      () => !!task.value.id && meetingRecording.isRecordingHere.value
    );
    const {
      release: releaseRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    } = useRecordingToolbar(meetingRecording, audioRecording, {
      toolbar: {
        compact: true,
        tint: "red",
        getTranscriptResetKey: () => task.value.id,
      },
    });
    releaser.defer(releaseRecordingToolbar);

    const displayRecurrencePromise = releaser.add(
      bindAsync(task, (task) =>
        Promise.resolve(displayRecurrenceDate(task, controller)).then((v) =>
          releasableRef(v)
        )
      )
    );
    const nextAllocationPromise = releaser.add(
      bindAsync(task, async (task) => {
        if (task) return await findNextAllocation(task, controller);
        else return releasableRef(undefined);
      })
    );
    const hierarchyPromise = releaser.add(
      bindAsync(task, async (task) =>
        releasableRef((await computeHierarchy(task, controller, profile)).value)
      )
    );
    const [nextAllocation, displayRecurrence, hierarchy] = await Promise.all([
      nextAllocationPromise,
      displayRecurrencePromise,
      hierarchyPromise,
    ]);

    const breadcrumbHome = computed((): BreadcrumbHome | undefined => {
      const ws = currentWorkspaceId.value;
      return ws ? { type: "workspace", id: ws } : undefined;
    });

    const setTriggerPlanEl = (el: HTMLElement) => {
      planButtonContainerEl.value = el;
    };
    const setTriggerMoreActionsEl = (el: HTMLElement) => {
      moreActionsButtonContainerEl.value = el;
    };
    const { displaySchedule, displayPostpone, taskStatus } = useDisplaySchedule(
      locale,
      task,
      nextAllocation
    );
    const displayPlan = computed(() =>
      taskStatus.value === "scheduled"
        ? displaySchedule.value
        : taskStatus.value === "postponed"
          ? displayPostpone.value
          : undefined
    );

    const mainActions = computed<TaskContextActions[]>(() => [
      ...(schedulable.value ? [taskActions.plan] : []),
      ...(task.value.occurrence ? [taskActions.openRecurrence] : []),
      ...(task.value.ignored || task.value.completed
        ? [taskActions.restore]
        : []),
    ]);
    const moreActions = computed<TaskContextActions[]>(() => [
      ...(!task.value.ignored && !task.value.completed
        ? [taskActions.complete]
        : []),
      ...(!task.value.ignored && !task.value.completed
        ? [taskActions.ignore]
        : []),
      {
        ...taskActions.move,
        state: task.value.canMove === false ? "disabled" : ("default" as const),
      },
      taskActions.repeat,
      taskActions.duplicate,
      taskActions.runAutomation,
      taskActions.copyLink,
      {
        ...taskActions.delete,
        tint: "red" as const,
        state:
          task.value.canDelete === false
            ? ("disabled" as const)
            : ("default" as const),
      },
    ]);
    const getActionFunc = (
      action: TaskContextActions,
      mouseEvent?: MouseEvent
    ) =>
      action.action(
        { type: "task", value: task.value },
        {
          controller,
          logger: baseLogger,
          profile,
          console: console.value,
          simpleModal,
          move,
          router,
          datePickerInject: {
            component: datePicker,
            triggerEl: planButtonContainerEl,
            onSelect: onSelectPlanDate,
            position: "right",
          },
          recurrencePickerInject: {
            component: recurrencePicker,
            triggerEl: moreActionsButtonContainerEl,
            onSelect: onSelectRecurrence,
            position: "right",
          },
          destinationPickerInject: {
            triggerEl: moreActionsButtonContainerEl,
            position: "right",
          },
          assigneePickerInject: {
            component: assigneePicker,
            triggerEl: moreActionsButtonContainerEl,
          },
          flush,
          menu,
          palette,
          clickEvent: mouseEvent,
        },
        {
          beforeAction:
            //TODO: Build a better way to compare, tint screws up action === pageActions.delete
            action.text === taskActions.delete.text
              ? async () => navigateBackward(router, menu)
              : undefined,
        }
      );
    const removeParentAction = computed(() => ({
      onRemove: () =>
        taskActions.removeParent.action(
          { type: "task", value: task.value },
          { controller }
        ),
      shortcut: taskActions.removeParent.shortcutsDisplay,
    }));
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      const base = hasBlockActive.value
        ? {}
        : {
            ...getTaskShortcuts(
              task.value,
              {
                controller,
                profile,
                logger: baseLogger,
                router,
                console: console.value,
                simpleModal,
                move,
                datePickerInject: {
                  component: datePicker,
                  triggerEl: planButtonContainerEl,
                  onSelect: onSelectPlanDate,
                  position: "right",
                },
                recurrencePickerInject: {
                  component: recurrencePicker,
                  triggerEl: moreActionsButtonContainerEl,
                  onSelect: onSelectRecurrence,
                },
                destinationPickerInject: {
                  triggerEl: moreActionsButtonContainerEl,
                  position: "right",
                },
                assigneePickerInject: {
                  component: assigneePicker,
                  triggerEl: moreActionsButtonContainerEl,
                },
                menu,
                palette,
                ...injection,
              },
              {
                flush,
                beforeDeleteAction: async () => navigateBackward(router, menu),
              }
            ),
            // Keep "Move" available even while the title/notes input is focused
            // (the title is auto-focused on the task page). This mirrors the
            // editor node-view, which keeps block shortcuts alive while editing.
            // We are already in the `!hasBlockActive` branch, so this never
            // double-fires with a focused task block's own move shortcut.
            ...assignShortcuts(
              { isModalShown, isInputFocused: ref(false) },
              taskActions.move.shortcuts,
              () => {
                getActionFunc(taskActions.move);
              },
              ref(task.value.canMove !== false)
            ),
            ...assignShortcuts(
              injection,
              taskActions.complete.shortcuts,
              () => {
                emit("checkTask");
              }
            ),
            ...assignShortcuts(
              injection,
              taskActions.runAutomation.shortcuts,
              () => {
                getActionFunc(taskActions.runAutomation);
              }
            ),
            ...assignShortcuts(injection, selectAllShortcut, () => {
              selectAll();
            }),
          };
      // Mod+F (find) is bound by BaseTopBar itself (`with-find`).
      return base;
    });
    const isPinned = computed(() => menu.isItemPinned(task.value.id));
    const togglePin = async () => {
      await menu.toggleObjectPin(task.value.id, "task");
    };

    const onExport = createBuiltinExportAction(controller, profile, () => ({
      title: task.value.title,
      notes: task.value.notes,
      extensionKind: "task",
      extensionRow: task.value.extension_row,
    }));

    const personalOrganizationId = computed(
      () => profile.value.user.personal.organizationId
    );
    // Marketplace packaging root. (A bound task's packaging entry point is
    // its own object page — bound tasks never reach this screen.)
    const shareRoot = computed<ExportRoot>(() => ({
      kind: "task",
      taskId: task.value.id,
      organizationId: personalOrganizationId.value,
    }));
    const planButtonContainerEl = ref<HTMLElement | undefined>(undefined);
    const moreActionsButtonContainerEl = ref<HTMLElement | undefined>(
      undefined
    );
    const isPlanPickerVisible = ref(false);

    const scheduledDate = computed(() => {
      return task.value?.scheduled instanceof Date
        ? task.value.scheduled
        : undefined;
    });

    const onSelectPlanDate = (
      data: {
        mode: "date" | "week" | "month";
        date?: Date;
        week?: Week;
      } | null
    ) => {
      // "Clear" (null) unplans the task.
      if (data === null) {
        getActionFunc(taskActions.unplan);
        return;
      }
      if (task.value) {
        const update =
          data.mode === "date"
            ? { scheduled: data.date }
            : { scheduled: data.week };

        controller.task.update(task.value.id, update, "app", null);
      }
      datePicker.value.closePicker();
    };

    const onSelectRecurrence = async (recurrence: Recurrence) => {
      if (task.value) {
        const recurrentTaskId = await controller.taskRecurrent.create(
          {
            title: task.value.title,
            parent: task.value.parent || null,
            recurrence,
          },
          { notify: true }
        );
        recurrencePicker.value.closePicker();
        await navigateToObject(router, controller, {
          kind: "task-rec",
          id: recurrentTaskId,
        });
      }
    };

    return {
      taskActions,
      displayRecurrence,
      displayPlan,
      getActionFunc,
      hierarchy,
      breadcrumbHome,
      isMounted,
      mainActions,
      menu,
      moreActions,
      onExport,
      personalOrganizationId,
      removeParentAction,
      shareRoot,
      shortcuts,
      taskStatus,
      isPinned,
      togglePin,
      isPlanPickerVisible,
      planButtonContainerEl,
      moreActionsButtonContainerEl,
      scheduledDate,
      onSelectPlanDate,
      onSelectRecurrence,
      setTriggerPlanEl,
      setTriggerMoreActionsEl,
      showRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "@/styles/context-actions.scss";
</style>
